import pt from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import Text from '../../components/ui/text';
import { useLocale } from '../../context/locale.context';
import { MicrocopyContext } from '../../context/microcopy.context';
import { getCopy } from '../../helpers/copy';
import { combine } from '../../helpers/styles';
import { useScroll } from '../../hooks/useScrollPosition';
import ArrowUpIcon from '../../images/arrowUp.svg';
import ArrowDownIcon from '../../images/arrowDown.svg';
import HamburguerIcon from '../../images/hamburguer.svg';
import HamburguerCloseIcon from '../../images/hamburguerClose.svg';
import LanguageGlobeIcon from '../../images/languageGlobe.svg';
import SearchIcon from '../../images/search.svg';
import Container from '../container';
import SearchOverlay from './components/searchOverlay';
import * as styles from './header.module.scss';
import Link from '/src/components/ui/link';
import CubeNavigation from '../cubeNavigation';
import Logo from '../../images/logo.svg';

const Header = ({ hasHero, siteSettings, hasWhiteNavigation }) => {
  const { metaNavigation, mainNavigation, searchPlaceholder } = siteSettings;
  const microcopy = useContext(MicrocopyContext);
  const mainNavi = useRef();
  const fixedNavi = useRef();
  const searchInput = useRef();
  const [isMainHidden, setIsMainHidden] = useState(!hasHero);
  const [searchVisible, setSearchVisible] = useState(false);
  const [headerOnTop, setHeaderOnTop] = useState(true);
  const [mobileMenuShowing, setMobileMenuShowing] = useState(false);
  const [mobileLanguageSelectShowing, setMobileLanguageSelectShowing] =
    useState(false);
  const [languageSwitchOpen, setLanguageSwitchOpen] = useState(false);
  const [subNavIndex, setSubNavIndex] = useState(-1);
  const [whiteMobileBackgroundNeeded, setWhiteMobileBackgroundNeeded] =
    useState(false);

  const { scrollY, scrollDirection } = useScroll();
  const { locale, languageToggleOptions } = useLocale();

  const isSearchEnabled = ['en'].includes(locale);

  useEffect(() => {
    if (scrollDirection === 'up') {
      setHeaderOnTop(true);
    } else if (scrollDirection == 'down') {
      setHeaderOnTop(false);
    }
  }, [scrollY, scrollDirection]);

  const handleSearchVisibility = () => {
    setSearchVisible(!searchVisible);
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuShowing(mobileLanguageSelectShowing || !mobileMenuShowing);
    setMobileLanguageSelectShowing(false);
  };

  const handleMobileLanguageSelectOpen = () => {
    setMobileMenuShowing(!mobileLanguageSelectShowing || !mobileMenuShowing);

    if (mobileMenuShowing && mobileLanguageSelectShowing) {
      setTimeout(() => {
        setMobileLanguageSelectShowing(!mobileLanguageSelectShowing);
      }, 300);
    } else {
      setMobileLanguageSelectShowing(!mobileLanguageSelectShowing);
    }
  };

  const hanldeSubMenuOpen = (e) => {
    const selectedIndex = e.currentTarget.getAttribute('mob-nav');
    const hasSubNav =
      e.currentTarget.getAttribute('mob-subnav') === 'has-sub' ? true : false;
    if (hasSubNav && subNavIndex !== selectedIndex) {
      setSubNavIndex(selectedIndex);
    } else {
      setSubNavIndex(-1);
      return;
    }
  };

  return (
    <>
      <header
        ref={mainNavi}
        className={combine(
          styles.header,
          searchVisible && styles.searchVisible,
          !headerOnTop && styles.notVisible
        )}
      >
        <div
          className={combine(
            styles.headerTop,
            whiteMobileBackgroundNeeded && styles.mobileBackground,
            isMainHidden && styles.isHidden
          )}
        >
          <Container fluid noGap className={styles.navContainer}>
            <nav className={styles.mainNavigation}>
              {
                <button
                  className={styles.hamburguerIcon}
                  onClick={handleMobileMenuOpen}
                >
                  <HamburguerIcon />
                </button>
              }
              <Link link={{ linkToPage: '/' }} className={styles.logoLink}>
                <Logo className={styles.logo} />
              </Link>
              <div className={styles.rightNavigation}>
                {metaNavigation.map((item, index) => (
                  <div className={styles.desktopNavItemContainer} key={index}>
                    <div
                      className={combine(
                        styles.mobileNavItemInnerContainer,
                        styles.metaNaviElements
                      )}
                    >
                      <Link link={item} className={combine(styles.navItemLink)}>
                        <Text
                          as="span"
                          type="metanavi"
                          text={item.text}
                          className={styles.linkText}
                        >
                          {item.text}
                        </Text>
                      </Link>
                    </div>
                  </div>
                ))}
                <div
                  id="languagePicker"
                  role="button"
                  className={combine(
                    styles.languagePicker,
                    languageSwitchOpen && styles.open
                  )}
                  onClick={() => {
                    setLanguageSwitchOpen(true);
                  }}
                  onMouseLeave={() => {
                    setLanguageSwitchOpen(false);
                  }}
                >
                  <div
                    className={combine(
                      styles.currentLang,
                      styles.languagePickerIcon
                    )}
                  >
                    <LanguageGlobeIcon />
                  </div>
                  <div className={styles.dropdownContainer}>
                    <ul>
                      {languageToggleOptions.map((loc) => (
                        <li
                          key={loc.url}
                          className={loc.locale === locale && styles.active}
                        >
                          <a href={loc.url} className={styles.localeLink}>
                            <Text
                              as="span"
                              type="metanavi"
                              text={loc.label}
                              className={styles.linkText}
                            >
                              {loc.label}
                            </Text>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div
                  role="button"
                  className={styles.languagePickerMobileToggle}
                  onClick={handleMobileLanguageSelectOpen}
                >
                  <LanguageGlobeIcon />
                </div>
                <input
                  className={styles.searchInput}
                  ref={searchInput}
                  placeholder={searchPlaceholder}
                ></input>
                <button
                  className={combine(
                    styles.searchIcon,
                    mobileMenuShowing && styles.searchMobileMenu,
                    !isSearchEnabled && styles.disabled
                  )}
                  onClick={handleSearchVisibility}
                >
                  <SearchIcon />
                </button>
              </div>
            </nav>
            <nav
              ref={fixedNavi}
              className={combine(
                styles.mobileMenu,
                mobileMenuShowing &&
                  !mobileLanguageSelectShowing &&
                  styles.opened
              )}
            >
              <div
                className={combine(
                  styles.mobileMenuContainer,
                  !mobileLanguageSelectShowing && styles.alignedLeft
                )}
              >
                {!mobileLanguageSelectShowing && (
                  <>
                    <button
                      className={styles.hamburguerClose}
                      onClick={handleMobileMenuOpen}
                    >
                      <HamburguerCloseIcon />
                    </button>
                    {mainNavigation.map((item, index) => (
                      <div
                        key={index}
                        className={styles.mobileNavItemOutterContainer}
                        mob-nav={index}
                        mob-subnav={
                          item.childItems && item.childItems.length > 0
                            ? 'has-sub'
                            : 'not-sub'
                        }
                        onClick={hanldeSubMenuOpen}
                      >
                        <div
                          className={combine(
                            styles.mobileNavItemInnerContainer,
                            Number(subNavIndex) === index && styles.activeLeft
                          )}
                        >
                          <div className={styles.navItem}>
                            <Text
                              as="span"
                              type="metanavi"
                              className={styles.linkText}
                            >
                              {item.link.text}
                            </Text>
                          </div>
                          {Number(subNavIndex) === index ? (
                            <ArrowUpIcon />
                          ) : (
                            <ArrowDownIcon />
                          )}
                        </div>
                        {item.childItems && item.childItems.length > 0 && (
                          <div
                            className={combine(
                              styles.submenuWrapperLeft,
                              Number(subNavIndex) === index && styles.activeLeft
                            )}
                            sub-index={index}
                            mobile-submenu-item="true"
                          >
                            <div className={styles.submenuInnerWrapper}>
                              <div className={styles.subMenuItems}>
                                {item.childItems.map((subItem) => (
                                  <div
                                    className={styles.navSubItemContainer}
                                    key={subItem.link.text}
                                  >
                                    <Link
                                      link={subItem.link}
                                      className={styles.navSubItem}
                                    >
                                      <Text as="span" type="metanavi">
                                        {subItem.link.text}
                                      </Text>
                                    </Link>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {metaNavigation.map((item, index) => (
                      <div
                        className={styles.mobileNavItemOutterContainer}
                        key={index}
                      >
                        <div
                          className={combine(
                            styles.mobileNavItemInnerContainer,
                            styles.metaNaviElements
                          )}
                        >
                          <Link
                            link={item}
                            className={combine(styles.navItemLink)}
                          >
                            <Text
                              as="span"
                              type="metanavi"
                              text={item.text}
                              className={styles.linkText}
                            >
                              {item.text}
                            </Text>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {!mobileLanguageSelectShowing &&
                  metaNavigation.map((item) => {
                    return (
                      <div
                        className={styles.mobileMetaOutterContainer}
                        key={item.text}
                      >
                        <div className={styles.mobileMetaInnerContainer}>
                          <Link link={item} className={styles.navItem}>
                            <Text as="span" type="metanavi">
                              {item.text}
                            </Text>
                          </Link>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </nav>
            <nav
              className={combine(
                styles.mobileMenu,
                styles.langSelector,
                mobileMenuShowing &&
                  mobileLanguageSelectShowing &&
                  styles.opened
              )}
            >
              <div className={combine(styles.mobileMenuContainer)}>
                <div className={styles.mobileNavItemOutterContainer}>
                  <div
                    className={combine(styles.submenuWrapper, styles.active)}
                  >
                    <div className={styles.submenuInnerWrapper}>
                      <div className={styles.submenuBackLinkContainer}>
                        <button
                          className={styles.hamburguerClose}
                          onClick={handleMobileLanguageSelectOpen}
                        >
                          <HamburguerCloseIcon />
                        </button>
                        <div
                          className={combine(
                            styles.submenuBackLink,
                            styles.languageMenu
                          )}
                        >
                          <Text as="span" type="metanavi">
                            {getCopy('label.chooseLanguage', microcopy)}
                          </Text>
                        </div>
                      </div>
                      <div className={styles.subMenuItems}>
                        {languageToggleOptions.map((loc) => (
                          <div
                            className={styles.navSubItemContainer}
                            key={loc.url}
                          >
                            <a
                              href={loc.url}
                              className={combine(
                                styles.navSubItem,
                                styles.localeLink,
                                loc.locale === locale && styles.active
                              )}
                            >
                              <Text as="span" type="metanavi">
                                {loc.label}
                              </Text>
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </nav>
          </Container>
        </div>
        <CubeNavigation
          navigationItems={siteSettings.mainNavigation}
          isWhite={hasWhiteNavigation}
        />
      </header>
      {searchVisible && (
        <SearchOverlay onClose={() => setSearchVisible(false)} />
      )}
    </>
  );
};

Header.propTypes = {
  hasWhiteNavigation: pt.bool,
  hasHero: pt.bool,
  siteSettings: pt.object,
};

Header.defaultProps = {
  hasHero: true,
  hasWhiteNavigation: true,
};

export default Header;
